import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { SITE, NAV_LINKS } from "@/lib/site";
import { FOOTER } from "@/constants/testIds";

const CYBER_ESSENTIALS_LOGO =
  "https://customer-assets.emergentagent.com/job_care-at-home-embrace/artifacts/e164x4wh_IMG_1789.png";

export const Footer = () => {
  return (
    <footer
      data-testid={FOOTER.root}
      className="mt-24 bg-brand text-white relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-deep/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-4 py-3">
              <img
                src={SITE.logo}
                alt="Embrace Healthcare"
                className="h-12 w-auto"
              />
            </div>
            <p className="font-display text-2xl lg:text-[1.65rem] mt-7 leading-snug text-white">
              Compassionate care, delivered with dignity — in the place you
              love most.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2">
                <ShieldCheck className="w-5 h-5 text-white" />
                <span className="text-sm tracking-wide uppercase font-semibold">
                  CQC Regulated Provider
                </span>
              </div>
              <div
                data-testid="footer-cyber-essentials"
                className="inline-flex items-center gap-3 rounded-2xl bg-white p-2 pr-4"
                title="Cyber Essentials Certified"
              >
                <img
                  src={CYBER_ESSENTIALS_LOGO}
                  alt="Cyber Essentials Certified"
                  className="h-10 w-auto"
                />
                <span className="text-brand-ink text-xs uppercase tracking-[0.15em] font-semibold leading-tight">
                  Cyber Essentials
                  <br />
                  Certified
                </span>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-display text-xl mb-5 text-white">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <Link
                    to={l.to}
                    data-testid={FOOTER.link(l.id)}
                    className="text-white/85 hover:text-white transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-xl mb-5 text-white">
              Doncaster office
            </h4>
            <ul className="space-y-3 text-white/90 text-[15px]">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-white shrink-0" />
                <a
                  href={SITE.offices[0].phoneHref}
                  data-testid={FOOTER.phone}
                  className="hover:text-white transition"
                >
                  {SITE.offices[0].phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-white shrink-0" />
                <span>
                  {SITE.offices[0].line1}
                  <br />
                  {SITE.offices[0].line2}
                  <br />
                  {SITE.offices[0].line3}
                  <br />
                  {SITE.offices[0].postcode}
                </span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-xl mb-5 text-white">
              Leicester office
            </h4>
            <ul
              className="space-y-3 text-white/90 text-[15px]"
              data-testid={FOOTER.address}
            >
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-white shrink-0" />
                <a
                  href={SITE.offices[1].phoneHref}
                  data-testid="footer-phone-leicester"
                  className="hover:text-white transition"
                >
                  {SITE.offices[1].phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-white shrink-0" />
                <span>
                  {SITE.offices[1].line1}
                  <br />
                  {SITE.offices[1].line2}
                  <br />
                  {SITE.offices[1].line3}
                  <br />
                  {SITE.offices[1].postcode}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-white shrink-0" />
                <a
                  href={SITE.emailHref}
                  data-testid={FOOTER.email}
                  className="hover:text-white transition break-all"
                >
                  {SITE.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-white/80">
          <p>
            © <time dateTime={new Date().getFullYear().toString()}>{new Date().getFullYear()}</time>{" "}
            {SITE.legalName}. All rights reserved.
          </p>
          <p>
            Doncaster • Leicester • United Kingdom
            <br />
            <span className="text-white/60">Created by </span>
            <a
              href="https://elitecreationsonline.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-elite-credit"
              className="underline underline-offset-4 decoration-white/40 hover:decoration-white text-white transition"
            >
              Elite Creations
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
